import { useState, useEffect } from 'react';
import {
  Modal,
  Button,
  TextInput,
  Group,
  Select
} from '@mantine/core';
import { useTranslation } from 'react-i18next';

export type ClientContact = {
  id: number;
  legal_name: string;
  contact_id?: string;
  client_id?: string;
  email?: string;
  phone_number?: string;
};

export const initialClientContactForm: ClientContact = {
  id: 0,
  legal_name: '',
  contact_id: '',
  client_id: '',
  email: '',
  phone_number: '',
};

export type ClientContactProps = {
  opened: boolean;
  onClose: () => void;
  onSave: (clientContact: ClientContact) => void;
  onDelete?: (index: number) => void;
  isEditingClientContact: boolean;
  editingIndex: number;
  initialClientContact?: ClientContact;
  clients: Set<string>;
};


export const ClientContactModal: React.FC<ClientContactProps> = ({
  opened,
  onClose,
  onSave,
  onDelete,
  isEditingClientContact,
  editingIndex,
  initialClientContact,
  clients,
}) => {
  const { t } = useTranslation();

  const [clientContact, setClientContact] = useState<ClientContact>(
    initialClientContact || { ...initialClientContactForm }
  );

  useEffect(() => {
    if (opened) {
      setClientContact(initialClientContact ? { ...initialClientContact } : { ...initialClientContactForm });
    }
  }, [opened, initialClientContact]);

  const handleSave = () => {
    onSave(clientContact);
  };

  const handleDelete = () => {
    onDelete?.(editingIndex);
  };

  const textFields = [
    { labelKey: 'contacts.form.email', key: 'email' },
    { labelKey: 'contacts.columns.phoneNumber', key: 'phone_number' },
  ] as const;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        isEditingClientContact
          ? t('contacts.clientContact.editTitle')
          : t('contacts.clientContact.createTitle')
      }
    >
      <Select
        label={t('contacts.clientContact.client')}
        placeholder={t('common.select')}
        value={clientContact.legal_name}
        onChange={(value) =>
          setClientContact((prev) => ({ ...prev, legal_name: value || '' }))
        }
        data={Array.from(clients).map((legal_name) => ({
          value: legal_name,
          label: legal_name,
        }))}
        required
        searchable
      />

      {textFields.map((field) => (
        <TextInput
          key={field.key}
          label={t(field.labelKey)}
          value={clientContact[field.key] || ''}
          onChange={(e) =>
            setClientContact((prev) => ({ ...prev, [field.key]: e.target.value }))
          }
        />
      ))}
      <Group position="right" mt="md">
        {isEditingClientContact && (
          <Button
            onClick={handleDelete}
            sx={{
              backgroundColor: 'rgba(163, 0, 0, 0.9)',
              color: 'white',
              '&:hover': { backgroundColor: 'rgba(121, 0, 0, 0.9)' },
            }}
          >
            {t('common.delete')}
          </Button>
        )}
        <Button onClick={handleSave}>
          {isEditingClientContact ? t('common.update') : t('common.save')}
        </Button>
      </Group>
    </Modal>
  );
};
